import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { traverse } from '@atlaskit/adf-utils/traverse/main';
import { sanitizeNode } from '@atlaskit/editor-json-transformer/sanitize/sanitize-node';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';

import {
	containsNativeEmbed,
	hasPreparedRemixableContent,
} from '@atlassian/malleable-ui-components/editor-remix/remix-fragment';
import { isRemixRestrictedNodeTypeWithExpands } from '@atlassian/malleable-ui-components/remix/remix-config';

/**
 * Filters an ADF fragment (array of JSONNode) by recursively removing nodes
 * whose type is in the Remix deny list at every level of the tree.
 * Returns null when given null.
 *
 * Uses the adf-utils `traverse` helper which drops nodes when a visitor
 * returns `false` and automatically rebuilds content arrays.
 *
 * Expands are the one deny-list exception: the expand wrapper
 * (title attr included) and its inner content are preserved so "remix this expand" keeps its
 * meaning. Everything nested inside is still filtered normally.
 */
export const filterRemixFragmentNodes = (nodes: JSONNode[] | null): JSONNode[] | null => {
	if (nodes === null) {
		return null;
	}

	const result: JSONNode[] = [];
	for (const node of nodes) {
		const filtered = traverse(node as ADFEntity, {
			any: (adfNode) => {
				if (isRemixRestrictedNodeTypeWithExpands(adfNode.type)) {
					return false;
				}
			},
		});
		if (filtered !== false) {
			result.push(filtered as JSONNode);
		}
	}
	return result;
};

/**
 * Converts editor selection JSON to canonical ADF before applying Remix's existing node filter.
 */
export const prepareRemixFragmentNodes = (nodes: JSONNode[] | null): JSONNode[] | null => {
	if (nodes === null) {
		return null;
	}

	const sanitizedNodes = nodes.flatMap((node) => {
		// The sanitizer can remove an invalid root node at runtime even though its legacy return
		// type only exposes JSONNode.
		const sanitizedNode = sanitizeNode(node, { keepNestedTables: true }) as JSONNode | false;
		return sanitizedNode === false ? [] : [sanitizedNode];
	});

	return filterRemixFragmentNodes(sanitizedNodes);
};

export const isRendererSelectionUnremixable = (
	fragment: JSONNode[] | null | undefined,
): boolean => {
	if (fragment == null || fragment.some(containsNativeEmbed)) {
		return false;
	}

	return !hasPreparedRemixableContent(prepareRemixFragmentNodes(fragment));
};
